import {
  FRAME_STYLE,
  MAX_DECIMALS_FOR_SVG_EXPORT,
  SVG_NS,
  THEME,
  DARK_THEME_FILTER,
  getFontFamilyString,
  isRTL,
  isTestEnv,
  getVerticalOffset,
  applyDarkModeFilter,
  MIME_TYPES,
} from "@excalidraw/common";
import { normalizeLink, toValidURL } from "@excalidraw/common";
import {
  getBoundsFromPoints,
  getBrushPadding,
  hashString,
  isBrushElement,
  renderBrushStroke,
} from "@excalidraw/element";
import {
  getLinearGradientEndpoints,
  getRadialGradientGeometry,
} from "@excalidraw/element";
import { getUncroppedWidthAndHeight } from "@excalidraw/element";
import {
  createPlaceholderEmbeddableLabel,
  getEmbedLink,
} from "@excalidraw/element";
import { LinearElementEditor } from "@excalidraw/element";
import { getBoundTextElement, getContainerElement } from "@excalidraw/element";
import { getLineHeightInPx } from "@excalidraw/element";
import { getRoughTextJitter } from "@excalidraw/element";
import {
  isArrowElement,
  isIframeLikeElement,
  isInitializedImageElement,
  isTextElement,
} from "@excalidraw/element";

import { getContainingFrame } from "@excalidraw/element";

import {
  getCornerRadii,
  isPathALoop,
  roundRectPath,
} from "@excalidraw/element";

import { ShapeCache } from "@excalidraw/element";

import { getElementAbsoluteCoords } from "@excalidraw/element";

import type {
  ExcalidrawElement,
  ExcalidrawFreeDrawElement,
  ExcalidrawTextElementWithContainer,
  NonDeletedExcalidrawElement,
} from "@excalidraw/element/types";

import type { RenderableElementsMap, SVGRenderConfig } from "../scene/types";
import type { AppState, BinaryFiles } from "../types";
import type { Drawable } from "roughjs/bin/core";
import type { RoughSVG } from "roughjs/bin/svg";

const roughSVGDrawWithPrecision = (
  rsvg: RoughSVG,
  drawable: Drawable,
  precision?: number,
) => {
  if (typeof precision === "undefined") {
    return rsvg.draw(drawable);
  }
  const pshape: Drawable = {
    sets: drawable.sets,
    shape: drawable.shape,
    options: { ...drawable.options, fixedDecimalPlaceDigits: precision },
  };
  return rsvg.draw(pshape);
};

const splitHexAlpha = (color: string) => {
  if (/^#[0-9a-fA-F]{8}$/.test(color)) {
    return {
      color: color.slice(0, 7),
      opacity: parseInt(color.slice(7, 9), 16) / 255,
    };
  }
  return { color, opacity: 1 };
};

// export quality multiplier for rasterized brush strokes (2x = crisp on
// retina without ballooning file size)
const BRUSH_SVG_EXPORT_SCALE = 2;

/**
 * Rasterizes a textured brush stroke (packages/element/src/brush) into an
 * offscreen canvas and wraps it in an <image>, positioned in the same
 * element-local coordinate space the freedraw <path> branch draws in
 * (element.x/y is the anchor, NOT the points bbox min — mirrors
 * getFreeDrawElementAbsoluteCoords, hence getBoundsFromPoints here rather
 * than assuming points start at 0,0). Returns null when canvas 2d is
 * unavailable or there's nothing to draw, so the caller can fall back to the
 * (untextured) vector path.
 */
const renderBrushStrokeToSvgImage = (
  element: ExcalidrawFreeDrawElement,
  svgRoot: SVGElement,
  strokeColor: string,
): SVGImageElement | null => {
  if (!isBrushElement(element) || element.points.length === 0) {
    return null;
  }

  const pad = getBrushPadding(element);
  const maskPoints = (element.eraserMasks ?? []).flatMap((m) => m.points);
  const [minX, minY, maxX, maxY] = getBoundsFromPoints([
    ...element.points,
    ...maskPoints,
  ]);
  const width = maxX - minX + pad * 2;
  const height = maxY - minY + pad * 2;
  if (!(width > 0) || !(height > 0)) {
    return null;
  }

  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(width * BRUSH_SVG_EXPORT_SCALE);
  canvas.height = Math.ceil(height * BRUSH_SVG_EXPORT_SCALE);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.scale(BRUSH_SVG_EXPORT_SCALE, BRUSH_SVG_EXPORT_SCALE);
  ctx.translate(pad - minX, pad - minY);
  renderBrushStroke(ctx, element, strokeColor);

  const image = svgRoot.ownerDocument.createElementNS(SVG_NS, "image");
  image.setAttribute("href", canvas.toDataURL(MIME_TYPES.png));
  image.setAttribute("x", `${minX - pad}`);
  image.setAttribute("y", `${minY - pad}`);
  image.setAttribute("width", `${width}`);
  image.setAttribute("height", `${height}`);
  return image;
};

/**
 * Inject a <linearGradient>/<radialGradient> def and point the shape's fill
 * path at it. `node` is the <g> produced by roughjs for the shape.
 */
const applySvgBackgroundGradient = (
  element: ExcalidrawElement,
  node: SVGElement,
  svgRoot: SVGElement,
  isDark: boolean,
) => {
  const gradient = element.backgroundGradient;
  if (!gradient) {
    return;
  }
  const doc = svgRoot.ownerDocument;
  let defs = svgRoot.querySelector("defs");
  if (!defs) {
    defs = doc.createElementNS(SVG_NS, "defs");
    svgRoot.prepend(defs);
  }
  const id = `gradient-${element.id}`;
  defs.querySelector(`[id="${id}"]`)?.remove();

  let gradEl: SVGElement;
  if (gradient.type === "radial") {
    const { cx, cy, r } = getRadialGradientGeometry(
      element.width,
      element.height,
    );
    gradEl = doc.createElementNS(SVG_NS, "radialGradient");
    gradEl.setAttribute("cx", `${cx}`);
    gradEl.setAttribute("cy", `${cy}`);
    gradEl.setAttribute("r", `${r}`);
  } else {
    const { x1, y1, x2, y2 } = getLinearGradientEndpoints(
      element.width,
      element.height,
      gradient.angle,
    );
    gradEl = doc.createElementNS(SVG_NS, "linearGradient");
    gradEl.setAttribute("x1", `${x1}`);
    gradEl.setAttribute("y1", `${y1}`);
    gradEl.setAttribute("x2", `${x2}`);
    gradEl.setAttribute("y2", `${y2}`);
  }
  gradEl.setAttribute("id", id);
  gradEl.setAttribute("gradientUnits", "userSpaceOnUse");

  [...gradient.stops]
    .sort((a, b) => a.offset - b.offset)
    .forEach((stop) => {
      const { color, opacity } = splitHexAlpha(
        applyDarkModeFilter(stop.color, isDark),
      );
      const stopEl = doc.createElementNS(SVG_NS, "stop");
      stopEl.setAttribute(
        "offset",
        `${Math.min(100, Math.max(0, stop.offset * 100))}%`,
      );
      stopEl.setAttribute("stop-color", color);
      if (opacity !== 1) {
        stopEl.setAttribute("stop-opacity", `${opacity}`);
      }
      gradEl.appendChild(stopEl);
    });
  defs.appendChild(gradEl);

  // point the shape's solid fill path at the gradient
  const fillPath = [...node.querySelectorAll("path")].find((p) => {
    const f = p.getAttribute("fill");
    return f && f !== "none";
  });
  fillPath?.setAttribute("fill", `url(#${id})`);
};

/**
 * Inject a <linearGradient>/<radialGradient> def and point the shape's
 * stroke path(s) at it. Mirrors `applySvgBackgroundGradient`, but a stroke
 * may span MULTIPLE roughjs sub-paths (multi-pass sketchy strokes), so every
 * stroke-carrying path under `node` is retargeted, not just the first.
 */
const applySvgStrokeGradient = (
  element: ExcalidrawElement,
  node: SVGElement,
  svgRoot: SVGElement,
  isDark: boolean,
) => {
  const gradient = element.strokeGradient;
  if (!gradient) {
    return;
  }
  const doc = svgRoot.ownerDocument;
  let defs = svgRoot.querySelector("defs");
  if (!defs) {
    defs = doc.createElementNS(SVG_NS, "defs");
    svgRoot.prepend(defs);
  }
  const id = `stroke-gradient-${element.id}`;
  defs.querySelector(`[id="${id}"]`)?.remove();

  let gradEl: SVGElement;
  if (gradient.type === "radial") {
    const { cx, cy, r } = getRadialGradientGeometry(
      element.width,
      element.height,
    );
    gradEl = doc.createElementNS(SVG_NS, "radialGradient");
    gradEl.setAttribute("cx", `${cx}`);
    gradEl.setAttribute("cy", `${cy}`);
    gradEl.setAttribute("r", `${r}`);
  } else {
    const { x1, y1, x2, y2 } = getLinearGradientEndpoints(
      element.width,
      element.height,
      gradient.angle,
    );
    gradEl = doc.createElementNS(SVG_NS, "linearGradient");
    gradEl.setAttribute("x1", `${x1}`);
    gradEl.setAttribute("y1", `${y1}`);
    gradEl.setAttribute("x2", `${x2}`);
    gradEl.setAttribute("y2", `${y2}`);
  }
  gradEl.setAttribute("id", id);
  gradEl.setAttribute("gradientUnits", "userSpaceOnUse");

  [...gradient.stops]
    .sort((a, b) => a.offset - b.offset)
    .forEach((stop) => {
      const { color, opacity } = splitHexAlpha(
        applyDarkModeFilter(stop.color, isDark),
      );
      const stopEl = doc.createElementNS(SVG_NS, "stop");
      stopEl.setAttribute(
        "offset",
        `${Math.min(100, Math.max(0, stop.offset * 100))}%`,
      );
      stopEl.setAttribute("stop-color", color);
      if (opacity !== 1) {
        stopEl.setAttribute("stop-opacity", `${opacity}`);
      }
      gradEl.appendChild(stopEl);
    });
  defs.appendChild(gradEl);

  // point every stroke-carrying path at the gradient (strokes may be
  // multiple sub-paths, unlike a single fill region)
  [...node.querySelectorAll("path")]
    .filter((p) => {
      const s = p.getAttribute("stroke");
      return s && s !== "none";
    })
    .forEach((p) => p.setAttribute("stroke", `url(#${id})`));
};

/**
 * Inject a <filter> def mirroring the element's enabled effects and point the
 * node at it. Mirrors `applySvgBackgroundGradient`.
 *
 * Only shadows and layer blur cross over. Deliberately omitted, all for the
 * same reason — canvas/PNG (and the GIF/WebM exporters, which go through
 * `applyEffectsToCanvas`) are the source of truth for these:
 *  - background blur — SVG has no portable backdrop filter;
 *  - noise and texture — expressing them needs feImage+feTile over a data
 *    URL, which cannot rotate or offset the tile and is unsupported in
 *    Safari and in non-browser SVG renderers (resvg, librsvg). Emitting a
 *    filter that renders in Chrome and blank elsewhere is worse than
 *    uniformly not carrying it. ponytail: if SVG must carry textures,
 *    rasterize the whole textured element into an <image> the way
 *    `renderBrushStrokeToSvgImage` above does — exact, at the cost of
 *    vector-ness and file size.
 */
const applySvgEffects = (
  element: ExcalidrawElement,
  node: SVGElement,
  svgRoot: SVGElement,
) => {
  const effects = element.effects;
  if (
    !effects ||
    !(
      effects.dropShadow?.enabled ||
      effects.innerShadow?.enabled ||
      effects.layerBlur?.enabled
    )
  ) {
    return;
  }
  const doc = svgRoot.ownerDocument;
  let defs = svgRoot.querySelector("defs");
  if (!defs) {
    defs = doc.createElementNS(SVG_NS, "defs");
    svgRoot.prepend(defs);
  }
  const id = `effects-${element.id}`;
  defs.querySelector(`[id="${id}"]`)?.remove();

  const filter = doc.createElementNS(SVG_NS, "filter");
  filter.setAttribute("id", id);
  // generous region so blurs/shadows don't clip
  filter.setAttribute("x", "-100%");
  filter.setAttribute("y", "-100%");
  filter.setAttribute("width", "300%");
  filter.setAttribute("height", "300%");

  const primitive = (name: string, attrs: Record<string, string>) => {
    const el = doc.createElementNS(SVG_NS, name);
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value);
    }
    filter.appendChild(el);
    return el;
  };

  // graph is built back-to-front; track the current "source" result name
  let result = "SourceGraphic";

  const dropShadow = effects.dropShadow;
  if (dropShadow?.enabled) {
    const { color, opacity } = splitHexAlpha(dropShadow.color);
    if (dropShadow.spread > 0) {
      primitive("feMorphology", {
        in: "SourceAlpha",
        operator: "dilate",
        radius: `${dropShadow.spread}`,
        result: "eff-dilated",
      });
    }
    primitive("feDropShadow", {
      in: dropShadow.spread > 0 ? "eff-dilated" : result,
      dx: `${dropShadow.offsetX}`,
      dy: `${dropShadow.offsetY}`,
      stdDeviation: `${dropShadow.blur / 2}`,
      "flood-color": color,
      "flood-opacity": `${opacity}`,
      result: "eff-drop",
    });
    if (dropShadow.spread > 0) {
      // feDropShadow on the dilated alpha loses the original graphic —
      // composite it back on top
      primitive("feMerge", {}).append(
        (() => {
          const n = doc.createElementNS(SVG_NS, "feMergeNode");
          n.setAttribute("in", "eff-drop");
          return n;
        })(),
        (() => {
          const n = doc.createElementNS(SVG_NS, "feMergeNode");
          n.setAttribute("in", "SourceGraphic");
          return n;
        })(),
      );
      filter.lastElementChild!.setAttribute("result", "eff-drop");
    }
    result = "eff-drop";
  }

  const innerShadow = effects.innerShadow;
  if (innerShadow?.enabled) {
    const { color, opacity } = splitHexAlpha(innerShadow.color);
    // canonical inner-shadow chain: shadow of the inverted alpha, kept inside
    primitive("feOffset", {
      in: "SourceAlpha",
      dx: `${innerShadow.offsetX}`,
      dy: `${innerShadow.offsetY}`,
      result: "eff-inner-offset",
    });
    primitive("feGaussianBlur", {
      in: "eff-inner-offset",
      stdDeviation: `${(innerShadow.blur + innerShadow.spread) / 2}`,
      result: "eff-inner-blur",
    });
    primitive("feComposite", {
      in: "SourceAlpha",
      in2: "eff-inner-blur",
      operator: "out",
      result: "eff-inner-mask",
    });
    primitive("feFlood", {
      "flood-color": color,
      "flood-opacity": `${opacity}`,
      result: "eff-inner-color",
    });
    primitive("feComposite", {
      in: "eff-inner-color",
      in2: "eff-inner-mask",
      operator: "in",
      result: "eff-inner-shadow",
    });
    primitive("feComposite", {
      in: "eff-inner-shadow",
      in2: result,
      operator: "over",
      result: "eff-inner",
    });
    result = "eff-inner";
  }

  const layerBlur = effects.layerBlur;
  if (layerBlur?.enabled && layerBlur.radius > 0) {
    primitive("feGaussianBlur", {
      in: result,
      stdDeviation: `${layerBlur.radius / 2}`,
      result: "eff-blur",
    });
    result = "eff-blur";
  }

  defs.appendChild(filter);
  node.setAttribute("filter", `url(#${id})`);
};

const maybeWrapNodesInFrameClipPath = (
  element: NonDeletedExcalidrawElement,
  root: SVGElement,
  nodes: SVGElement[],
  frameRendering: AppState["frameRendering"],
  elementsMap: RenderableElementsMap,
) => {
  if (!frameRendering.enabled || !frameRendering.clip) {
    return null;
  }
  const frame = getContainingFrame(element, elementsMap);
  if (frame) {
    const g = root.ownerDocument.createElementNS(SVG_NS, "g");
    g.setAttributeNS(SVG_NS, "clip-path", `url(#${frame.id})`);
    nodes.forEach((node) => g.appendChild(node));
    return g;
  }

  return null;
};

const renderElementToSvg = (
  element: NonDeletedExcalidrawElement,
  elementsMap: RenderableElementsMap,
  rsvg: RoughSVG,
  svgRoot: SVGElement,
  files: BinaryFiles,
  offsetX: number,
  offsetY: number,
  renderConfig: SVGRenderConfig,
) => {
  const offset = { x: offsetX, y: offsetY };
  const [x1, y1, x2, y2] = getElementAbsoluteCoords(element, elementsMap);
  let cx = (x2 - x1) / 2 - (element.x - x1);
  let cy = (y2 - y1) / 2 - (element.y - y1);
  if (isTextElement(element)) {
    const container = getContainerElement(element, elementsMap);
    if (isArrowElement(container)) {
      const [x1, y1, x2, y2] = getElementAbsoluteCoords(container, elementsMap);

      const boundTextCoords = LinearElementEditor.getBoundTextElementPosition(
        container,
        element as ExcalidrawTextElementWithContainer,
        elementsMap,
      );
      cx = (x2 - x1) / 2 - (boundTextCoords.x - x1);
      cy = (y2 - y1) / 2 - (boundTextCoords.y - y1);
      offsetX = offsetX + boundTextCoords.x - element.x;
      offsetY = offsetY + boundTextCoords.y - element.y;
    }
  }
  const degree = (180 * element.angle) / Math.PI;

  // element to append node to, most of the time svgRoot
  let root = svgRoot;

  // if the element has a link, create an anchor tag and make that the new root
  if (element.link) {
    const anchorTag = svgRoot.ownerDocument.createElementNS(SVG_NS, "a");
    anchorTag.setAttribute("href", normalizeLink(element.link));
    root.appendChild(anchorTag);
    root = anchorTag;
  }

  const addToRoot = (node: SVGElement, element: ExcalidrawElement) => {
    if (isTestEnv()) {
      node.setAttribute("data-id", element.id);
    }
    applySvgEffects(element, node, svgRoot);
    root.appendChild(node);
  };

  const opacity =
    ((getContainingFrame(element, elementsMap)?.opacity ?? 100) *
      element.opacity) /
    10000;

  switch (element.type) {
    case "selection": {
      // Since this is used only during editing experience, which is canvas based,
      // this should not happen
      throw new Error("Selection rendering is not supported for SVG");
    }
    case "rectangle":
    case "diamond":
    case "ellipse": {
      const shape = ShapeCache.generateElementShape(element, renderConfig);
      const node = roughSVGDrawWithPrecision(
        rsvg,
        shape,
        MAX_DECIMALS_FOR_SVG_EXPORT,
      );
      if (opacity !== 1) {
        node.setAttribute("stroke-opacity", `${opacity}`);
        node.setAttribute("fill-opacity", `${opacity}`);
      }
      node.setAttribute("stroke-linecap", "round");
      node.setAttribute(
        "transform",
        `translate(${offsetX || 0} ${
          offsetY || 0
        }) rotate(${degree} ${cx} ${cy})`,
      );

      applySvgBackgroundGradient(
        element,
        node,
        svgRoot,
        renderConfig.exportWithDarkMode,
      );
      applySvgStrokeGradient(
        element,
        node,
        svgRoot,
        renderConfig.exportWithDarkMode,
      );

      const g = maybeWrapNodesInFrameClipPath(
        element,
        root,
        [node],
        renderConfig.frameRendering,
        elementsMap,
      );

      addToRoot(g || node, element);
      break;
    }
    case "iframe":
    case "embeddable": {
      // render placeholder rectangle
      const shape = ShapeCache.generateElementShape(element, renderConfig);
      const node = roughSVGDrawWithPrecision(
        rsvg,
        shape,
        MAX_DECIMALS_FOR_SVG_EXPORT,
      );
      const opacity = element.opacity / 100;
      if (opacity !== 1) {
        node.setAttribute("stroke-opacity", `${opacity}`);
        node.setAttribute("fill-opacity", `${opacity}`);
      }
      node.setAttribute("stroke-linecap", "round");
      node.setAttribute(
        "transform",
        `translate(${offsetX || 0} ${
          offsetY || 0
        }) rotate(${degree} ${cx} ${cy})`,
      );
      addToRoot(node, element);

      const label: ExcalidrawElement =
        createPlaceholderEmbeddableLabel(element);
      renderElementToSvg(
        label,
        elementsMap,
        rsvg,
        root,
        files,
        label.x + offset.x - element.x,
        label.y + offset.y - element.y,
        renderConfig,
      );

      // render embeddable element + iframe
      const embeddableNode = roughSVGDrawWithPrecision(
        rsvg,
        shape,
        MAX_DECIMALS_FOR_SVG_EXPORT,
      );
      embeddableNode.setAttribute("stroke-linecap", "round");
      embeddableNode.setAttribute(
        "transform",
        `translate(${offsetX || 0} ${
          offsetY || 0
        }) rotate(${degree} ${cx} ${cy})`,
      );
      while (embeddableNode.firstChild) {
        embeddableNode.removeChild(embeddableNode.firstChild);
      }
      const radius = getCornerRadii(
        Math.min(element.width, element.height),
        element,
      )
        .map((r) => `${r}px`)
        .join(" ");

      const embedLink = getEmbedLink(toValidURL(element.link || ""));

      // if rendering embeddables explicitly disabled or
      // embedding documents via srcdoc (which doesn't seem to work for SVGs)
      // replace with a link instead
      if (
        renderConfig.renderEmbeddables === false ||
        embedLink?.type === "document"
      ) {
        const anchorTag = svgRoot.ownerDocument.createElementNS(SVG_NS, "a");
        anchorTag.setAttribute("href", normalizeLink(element.link || ""));
        anchorTag.setAttribute("target", "_blank");
        anchorTag.setAttribute("rel", "noopener noreferrer");
        anchorTag.style.borderRadius = radius;

        embeddableNode.appendChild(anchorTag);
      } else {
        const foreignObject = svgRoot.ownerDocument.createElementNS(
          SVG_NS,
          "foreignObject",
        );
        foreignObject.style.width = `${element.width}px`;
        foreignObject.style.height = `${element.height}px`;
        foreignObject.style.border = "none";
        const div = foreignObject.ownerDocument.createElementNS(SVG_NS, "div");
        div.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
        div.style.width = "100%";
        div.style.height = "100%";
        const iframe = div.ownerDocument.createElement("iframe");
        iframe.src = embedLink?.link ?? "";
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.style.border = "none";
        iframe.style.borderRadius = radius;
        iframe.style.top = "0";
        iframe.style.left = "0";
        iframe.allowFullscreen = true;
        div.appendChild(iframe);
        foreignObject.appendChild(div);

        embeddableNode.appendChild(foreignObject);
      }
      addToRoot(embeddableNode, element);
      break;
    }
    case "line":
    case "arrow": {
      const boundText = getBoundTextElement(element, elementsMap);
      const maskPath = svgRoot.ownerDocument.createElementNS(SVG_NS, "mask");
      if (boundText) {
        maskPath.setAttribute("id", `mask-${element.id}`);
        const maskRectVisible = svgRoot.ownerDocument.createElementNS(
          SVG_NS,
          "rect",
        );
        offsetX = offsetX || 0;
        offsetY = offsetY || 0;
        // Pin the mask to user space; the default maskUnits="objectBoundingBox"
        // collapses to zero area for axis-aligned arrows (zero-size bbox),
        // hiding the whole line from SVG exports (#11439).
        maskPath.setAttribute("maskUnits", "userSpaceOnUse");
        maskPath.setAttribute("x", "0");
        maskPath.setAttribute("y", "0");
        maskPath.setAttribute("width", `${element.width + 100 + offsetX}`);
        maskPath.setAttribute("height", `${element.height + 100 + offsetY}`);
        maskRectVisible.setAttribute("x", "0");
        maskRectVisible.setAttribute("y", "0");
        maskRectVisible.setAttribute("fill", "#fff");
        maskRectVisible.setAttribute(
          "width",
          `${element.width + 100 + offsetX}`,
        );
        maskRectVisible.setAttribute(
          "height",
          `${element.height + 100 + offsetY}`,
        );

        maskPath.appendChild(maskRectVisible);
        const maskRectInvisible = svgRoot.ownerDocument.createElementNS(
          SVG_NS,
          "rect",
        );
        const boundTextCoords = LinearElementEditor.getBoundTextElementPosition(
          element,
          boundText,
          elementsMap,
        );

        const maskX = offsetX + boundTextCoords.x - element.x;
        const maskY = offsetY + boundTextCoords.y - element.y;

        maskRectInvisible.setAttribute("x", maskX.toString());
        maskRectInvisible.setAttribute("y", maskY.toString());
        maskRectInvisible.setAttribute("fill", "#000");
        maskRectInvisible.setAttribute("width", `${boundText.width}`);
        maskRectInvisible.setAttribute("height", `${boundText.height}`);
        maskRectInvisible.setAttribute("opacity", "1");
        maskPath.appendChild(maskRectInvisible);
      }
      const group = svgRoot.ownerDocument.createElementNS(SVG_NS, "g");
      if (boundText) {
        group.setAttribute("mask", `url(#mask-${element.id})`);
      }
      group.setAttribute("stroke-linecap", "round");

      const shapes = ShapeCache.generateElementShape(element, renderConfig);
      shapes.forEach((shape) => {
        const node = roughSVGDrawWithPrecision(
          rsvg,
          shape,
          MAX_DECIMALS_FOR_SVG_EXPORT,
        );
        if (opacity !== 1) {
          node.setAttribute("stroke-opacity", `${opacity}`);
          node.setAttribute("fill-opacity", `${opacity}`);
        }
        node.setAttribute(
          "transform",
          `translate(${offsetX || 0} ${
            offsetY || 0
          }) rotate(${degree} ${cx} ${cy})`,
        );
        if (
          element.type === "line" &&
          isPathALoop(element.points) &&
          element.backgroundColor !== "transparent"
        ) {
          node.setAttribute("fill-rule", "evenodd");
        }
        group.appendChild(node);
      });

      applySvgStrokeGradient(
        element,
        group,
        svgRoot,
        renderConfig.exportWithDarkMode,
      );

      const g = maybeWrapNodesInFrameClipPath(
        element,
        root,
        [group, maskPath],
        renderConfig.frameRendering,
        elementsMap,
      );
      if (g) {
        addToRoot(g, element);
        root.appendChild(g);
      } else {
        addToRoot(group, element);
        root.append(maskPath);
      }
      break;
    }
    case "freedraw": {
      if (isBrushElement(element)) {
        const image = renderBrushStrokeToSvgImage(
          element,
          svgRoot,
          applyDarkModeFilter(
            element.strokeColor,
            renderConfig.theme === THEME.DARK,
          ),
        );
        if (image) {
          if (opacity !== 1) {
            image.setAttribute("opacity", `${opacity}`);
          }
          image.setAttribute(
            "transform",
            `translate(${offsetX || 0} ${
              offsetY || 0
            }) rotate(${degree} ${cx} ${cy})`,
          );
          const g = maybeWrapNodesInFrameClipPath(
            element,
            root,
            [image],
            renderConfig.frameRendering,
            elementsMap,
          );
          addToRoot(g || image, element);
          break;
        }
        // ctx unavailable → fall through to vector path (untextured but present)
      }

      const wrapper = svgRoot.ownerDocument.createElementNS(SVG_NS, "g");

      const shapes = ShapeCache.generateElementShape(element, renderConfig);
      // always ordered as [background, stroke]
      for (const shape of shapes) {
        if (typeof shape === "string") {
          // stroke (SVGPathString)

          const path = svgRoot.ownerDocument.createElementNS(SVG_NS, "path");
          path.setAttribute(
            "fill",
            applyDarkModeFilter(
              element.strokeColor,
              renderConfig.theme === THEME.DARK,
            ),
          );
          path.setAttribute("d", shape);
          wrapper.appendChild(path);
        } else {
          // background (Drawable)

          const bgNode = roughSVGDrawWithPrecision(
            rsvg,
            shape,
            MAX_DECIMALS_FOR_SVG_EXPORT,
          );

          // if children wrapped in <g>, unwrap it
          if (bgNode.nodeName === "g") {
            while (bgNode.firstChild) {
              wrapper.appendChild(bgNode.firstChild);
            }
          } else {
            wrapper.appendChild(bgNode);
          }
        }
      }
      if (opacity !== 1) {
        wrapper.setAttribute("stroke-opacity", `${opacity}`);
        wrapper.setAttribute("fill-opacity", `${opacity}`);
      }
      wrapper.setAttribute(
        "transform",
        `translate(${offsetX || 0} ${
          offsetY || 0
        }) rotate(${degree} ${cx} ${cy})`,
      );
      wrapper.setAttribute("stroke", "none");

      const g = maybeWrapNodesInFrameClipPath(
        element,
        root,
        [wrapper],
        renderConfig.frameRendering,
        elementsMap,
      );

      addToRoot(g || wrapper, element);
      break;
    }
    case "image": {
      const width = Math.round(element.width);
      const height = Math.round(element.height);
      const fileData =
        isInitializedImageElement(element) && files[element.fileId];
      if (fileData) {
        const { reuseImages = true } = renderConfig;

        let symbolId = `image-${fileData.id}`;

        let uncroppedWidth = element.width;
        let uncroppedHeight = element.height;
        if (element.crop) {
          ({ width: uncroppedWidth, height: uncroppedHeight } =
            getUncroppedWidthAndHeight(element));

          symbolId = `image-crop-${fileData.id}-${hashString(
            `${uncroppedWidth}x${uncroppedHeight}`,
          )}`;
        }

        if (!reuseImages) {
          symbolId = `image-${element.id}`;
        }

        let symbol = svgRoot.querySelector(`#${symbolId}`);
        if (!symbol) {
          symbol = svgRoot.ownerDocument.createElementNS(SVG_NS, "symbol");
          symbol.id = symbolId;

          const image = svgRoot.ownerDocument.createElementNS(SVG_NS, "image");
          image.setAttribute("href", fileData.dataURL);
          image.setAttribute("preserveAspectRatio", "none");

          if (element.crop || !reuseImages) {
            image.setAttribute("width", `${uncroppedWidth}`);
            image.setAttribute("height", `${uncroppedHeight}`);
          } else {
            image.setAttribute("width", "100%");
            image.setAttribute("height", "100%");
          }

          symbol.appendChild(image);

          (root.querySelector("defs") || root).prepend(symbol);
        }

        const use = svgRoot.ownerDocument.createElementNS(SVG_NS, "use");
        use.setAttribute("href", `#${symbolId}`);

        let normalizedCropX = 0;
        let normalizedCropY = 0;

        if (element.crop) {
          const { width: uncroppedWidth, height: uncroppedHeight } =
            getUncroppedWidthAndHeight(element);
          normalizedCropX =
            element.crop.x / (element.crop.naturalWidth / uncroppedWidth);
          normalizedCropY =
            element.crop.y / (element.crop.naturalHeight / uncroppedHeight);
        }

        const adjustedCenterX = cx + normalizedCropX;
        const adjustedCenterY = cy + normalizedCropY;

        use.setAttribute("width", `${width + normalizedCropX}`);
        use.setAttribute("height", `${height + normalizedCropY}`);
        use.setAttribute("opacity", `${opacity}`);

        // We first apply `scale` transforms (horizontal/vertical mirroring)
        // on the <use> element, then apply translation and rotation
        // on the <g> element which wraps the <use>.
        // Doing this separately is a quick hack to to work around compositing
        // the transformations correctly (the transform-origin was not being
        // applied correctly).
        if (element.scale[0] !== 1 || element.scale[1] !== 1) {
          use.setAttribute(
            "transform",
            `translate(${adjustedCenterX} ${adjustedCenterY}) scale(${
              element.scale[0]
            } ${
              element.scale[1]
            }) translate(${-adjustedCenterX} ${-adjustedCenterY})`,
          );
        }

        const g = svgRoot.ownerDocument.createElementNS(SVG_NS, "g");

        if (
          renderConfig.theme === THEME.DARK &&
          fileData.mimeType === MIME_TYPES.svg
        ) {
          g.setAttribute("filter", DARK_THEME_FILTER);
        }

        if (element.crop) {
          const mask = svgRoot.ownerDocument.createElementNS(SVG_NS, "mask");
          mask.setAttribute("id", `mask-image-crop-${element.id}`);
          mask.setAttribute("fill", "#fff");
          const maskRect = svgRoot.ownerDocument.createElementNS(
            SVG_NS,
            "rect",
          );

          maskRect.setAttribute("x", `${normalizedCropX}`);
          maskRect.setAttribute("y", `${normalizedCropY}`);
          maskRect.setAttribute("width", `${width}`);
          maskRect.setAttribute("height", `${height}`);

          mask.appendChild(maskRect);
          root.appendChild(mask);
          g.setAttribute("mask", `url(#${mask.id})`);
        }

        g.appendChild(use);
        g.setAttribute(
          "transform",
          `translate(${offsetX - normalizedCropX} ${
            offsetY - normalizedCropY
          }) rotate(${degree} ${adjustedCenterX} ${adjustedCenterY})`,
        );

        if (element.roundness) {
          const clipPath = svgRoot.ownerDocument.createElementNS(
            SVG_NS,
            "clipPath",
          );
          clipPath.id = `image-clipPath-${element.id}`;
          clipPath.setAttribute("clipPathUnits", "userSpaceOnUse");
          const radii = getCornerRadii(
            Math.min(element.width, element.height),
            element,
          );
          const clipOffsetX = element.crop ? normalizedCropX : 0;
          const clipOffsetY = element.crop ? normalizedCropY : 0;
          if (element.roundness.corners) {
            // independent corners can't be expressed via rect's rx/ry
            const clipShape = svgRoot.ownerDocument.createElementNS(
              SVG_NS,
              "path",
            );
            clipShape.setAttribute(
              "d",
              roundRectPath(element.width, element.height, radii),
            );
            clipShape.setAttribute(
              "transform",
              `translate(${clipOffsetX} ${clipOffsetY})`,
            );
            clipPath.appendChild(clipShape);
          } else {
            const clipRect = svgRoot.ownerDocument.createElementNS(
              SVG_NS,
              "rect",
            );
            clipRect.setAttribute("x", `${clipOffsetX}`);
            clipRect.setAttribute("y", `${clipOffsetY}`);
            clipRect.setAttribute("width", `${element.width}`);
            clipRect.setAttribute("height", `${element.height}`);
            clipRect.setAttribute("rx", `${radii[0]}`);
            clipRect.setAttribute("ry", `${radii[0]}`);
            clipPath.appendChild(clipRect);
          }
          addToRoot(clipPath, element);

          g.setAttributeNS(SVG_NS, "clip-path", `url(#${clipPath.id})`);
        }

        const clipG = maybeWrapNodesInFrameClipPath(
          element,
          root,
          [g],
          renderConfig.frameRendering,
          elementsMap,
        );
        addToRoot(clipG || g, element);
      }
      break;
    }
    // frames are not rendered and only acts as a container
    case "frame":
    case "magicframe": {
      if (
        renderConfig.frameRendering.enabled &&
        renderConfig.frameRendering.outline
      ) {
        const rect = document.createElementNS(SVG_NS, "rect");

        rect.setAttribute(
          "transform",
          `translate(${offsetX || 0} ${
            offsetY || 0
          }) rotate(${degree} ${cx} ${cy})`,
        );

        rect.setAttribute("width", `${element.width}px`);
        rect.setAttribute("height", `${element.height}px`);
        // Rounded corners
        rect.setAttribute("rx", FRAME_STYLE.radius.toString());
        rect.setAttribute("ry", FRAME_STYLE.radius.toString());

        rect.setAttribute("fill", "none");
        rect.setAttribute(
          "stroke",
          applyDarkModeFilter(
            FRAME_STYLE.strokeColor,
            renderConfig.theme === THEME.DARK,
          ),
        );
        rect.setAttribute("stroke-width", FRAME_STYLE.strokeWidth.toString());

        addToRoot(rect, element);
      }
      break;
    }
    default: {
      if (isTextElement(element)) {
        const node = svgRoot.ownerDocument.createElementNS(SVG_NS, "g");
        if (opacity !== 1) {
          node.setAttribute("stroke-opacity", `${opacity}`);
          node.setAttribute("fill-opacity", `${opacity}`);
        }

        node.setAttribute(
          "transform",
          `translate(${offsetX || 0} ${
            offsetY || 0
          }) rotate(${degree} ${cx} ${cy})`,
        );
        const lines = element.text.replace(/\r\n?/g, "\n").split("\n");
        const lineHeightPx = getLineHeightInPx(
          element.fontSize,
          element.lineHeight,
        );
        const horizontalOffset =
          element.textAlign === "center"
            ? element.width / 2
            : element.textAlign === "right"
            ? element.width
            : 0;
        const verticalOffset = getVerticalOffset(
          element.fontFamily,
          element.fontSize,
          lineHeightPx,
        );
        const direction = isRTL(element.text) ? "rtl" : "ltr";
        const textAnchor =
          element.textAlign === "center"
            ? "middle"
            : element.textAlign === "right" || direction === "rtl"
            ? "end"
            : "start";
        // rough handwriting: SVG applies dx/dy per glyph *cumulatively*, so
        // emit deltas between successive glyph offsets (rotate is absolute)
        let roughCharIndex = 0;
        for (let i = 0; i < lines.length; i++) {
          const text = svgRoot.ownerDocument.createElementNS(SVG_NS, "text");
          text.textContent = lines[i];
          if (element.roughText) {
            const dx: string[] = [];
            const dy: string[] = [];
            const rotate: string[] = [];
            let prevX = 0;
            let prevY = 0;
            for (let c = 0; c < Array.from(lines[i]).length; c++) {
              const jitter = getRoughTextJitter(
                element.seed,
                roughCharIndex++,
                element.fontSize,
                element.roughness,
              );
              dx.push((jitter.dx - prevX).toFixed(2));
              dy.push((jitter.dy - prevY).toFixed(2));
              rotate.push(((jitter.rotate * 180) / Math.PI).toFixed(2));
              prevX = jitter.dx;
              prevY = jitter.dy;
            }
            roughCharIndex++; // count the newline, same as the canvas path
            text.setAttribute("dx", dx.join(" "));
            text.setAttribute("dy", dy.join(" "));
            text.setAttribute("rotate", rotate.join(" "));
          }
          text.setAttribute("x", `${horizontalOffset}`);
          text.setAttribute("y", `${i * lineHeightPx + verticalOffset}`);
          text.setAttribute("font-family", getFontFamilyString(element));
          text.setAttribute("font-size", `${element.fontSize}px`);
          text.setAttribute(
            "fill",
            applyDarkModeFilter(
              element.strokeColor,
              renderConfig.theme === THEME.DARK,
            ),
          );
          text.setAttribute("text-anchor", textAnchor);
          text.setAttribute("style", "white-space: pre;");
          text.setAttribute("direction", direction);
          text.setAttribute("dominant-baseline", "alphabetic");
          node.appendChild(text);
        }

        const g = maybeWrapNodesInFrameClipPath(
          element,
          root,
          [node],
          renderConfig.frameRendering,
          elementsMap,
        );

        addToRoot(g || node, element);
      } else {
        // @ts-ignore
        throw new Error(`Unimplemented type ${element.type}`);
      }
    }
  }
};

export const renderSceneToSvg = (
  elements: readonly NonDeletedExcalidrawElement[],
  elementsMap: RenderableElementsMap,
  rsvg: RoughSVG,
  svgRoot: SVGElement,
  files: BinaryFiles,
  renderConfig: SVGRenderConfig,
) => {
  if (!svgRoot) {
    return;
  }

  // render elements
  elements
    .filter((el) => !isIframeLikeElement(el))
    .forEach((element) => {
      if (!element.isDeleted) {
        if (
          isTextElement(element) &&
          element.containerId &&
          elementsMap.has(element.containerId)
        ) {
          // will be rendered with the container
          return;
        }

        try {
          renderElementToSvg(
            element,
            elementsMap,
            rsvg,
            svgRoot,
            files,
            element.x + renderConfig.offsetX,
            element.y + renderConfig.offsetY,
            renderConfig,
          );

          const boundTextElement = getBoundTextElement(element, elementsMap);
          if (boundTextElement) {
            renderElementToSvg(
              boundTextElement,
              elementsMap,
              rsvg,
              svgRoot,
              files,
              boundTextElement.x + renderConfig.offsetX,
              boundTextElement.y + renderConfig.offsetY,
              renderConfig,
            );
          }
        } catch (error: any) {
          console.error(error);
        }
      }
    });

  // render embeddables on top
  elements
    .filter((el) => isIframeLikeElement(el))
    .forEach((element) => {
      if (!element.isDeleted) {
        try {
          renderElementToSvg(
            element,
            elementsMap,
            rsvg,
            svgRoot,
            files,
            element.x + renderConfig.offsetX,
            element.y + renderConfig.offsetY,
            renderConfig,
          );
        } catch (error: any) {
          console.error(error);
        }
      }
    });
};
